// All Inferra landing-page content (mock/static, no backend).
// Content adapted from the provided Inferra Landing Page artifact.

export const nav = {
  brand: "Inferra",
  links: [
    { label: "Products", href: "#lifecycle" },
    { label: "How it works", href: "#how" },
    { label: "Security", href: "#perimeter" },
    { label: "Questions", href: "#faq" },
  ],
  cta: { label: "Request a briefing", href: "#briefing" },
};

export const hero = {
  kicker: "credit ai, on-prem",
  titleTop: "credit",
  titleBottom: "committee-grade",
  heading: "AI your credit committee can approve.",
  body: "Inferra runs small, validated models inside your bank. It spreads financials, drafts credit papers and keeps every model inside policy. Borrower data never leaves your perimeter.",
  primary: { label: "Request a briefing", href: "#briefing" },
  secondary: { label: "Book a model audit", href: "#briefing" },
};

export const trust = {
  note: "Built on Credit Predix, in bank production for more than 20 years across 14 institutions.",
  logos: ["OCBC", "DBS", "Bank Muamalat", "Agrobank"],
};

// Full-screen "statement" moments (codos-style transitions)
export const statements = {
  savings: {
    bg: "var(--peach)",
    heading: "Borrower data never leaves the building.",
    sub: "Small models, run on hardware you already own.",
    blobs: [
      { x: 0.5, y: 0.42, r: 0.3 },
      { x: 0.62, y: 0.58, r: 0.24 },
    ],
  },
  existential: {
    bg: "var(--sage)",
    heading: "Every number traced back to its page.",
    sub: "Evidence for the committee, not a black box.",
    blobs: [{ x: 0.5, y: 0.5, r: 0.34 }],
  },
};

export const howIntro = {
  heading: "From statement to signed decision.",
  body: "Most banks now carry an AI mandate. The model risk committee still has to sign. Inferra is built for the committee first, so every step leaves evidence behind it.",
};

export const steps = [
  {
    id: "spread",
    step: "Step 1",
    eyebrow: "Read the file, with Spread",
    small: "Turn audited accounts into a clean, sourced spread.",
    heading: "Every number traced to its page.",
    body: "Spread reads audited accounts and management statements into your own chart-of-accounts template. Each figure links to its source line. E-invoice records check reported revenue before an analyst sees the file.",
  },
  {
    id: "memo",
    step: "Step 2",
    eyebrow: "Write the paper, with Memo",
    small: "Draft the committee paper from the structured file.",
    heading: "A credit paper your analysts sign, not rewrite.",
    body: "Memo drafts the committee paper from the structured file: financial analysis, risks, covenants and a recommendation. Every sentence cites its source. An analyst edits, a checker approves, and the trail stays with the file.",
  },
  {
    id: "validate",
    step: "Step 3",
    eyebrow: "Prove the model, with Validate",
    small: "Continuous validation, formatted for your regulator.",
    heading: "Validation that does not wait for the annual review.",
    body: "Validate watches every scorecard and model in your inventory, including third-party ones. Discrimination, stability and drift are tracked continuously, and the dossier comes formatted for your regulator.",
  },
];

// ---- dashboard mock data ----
export const workbench = {
  breadcrumb: "Corporate banking  /  Kestrel Freight Holdings  /  Annual renewal",
  analyst: "AR",
  tabs: ["Borrower file", "Spread", "Memo", "Rating", "Covenants", "Audit trail"],
  activeTab: "Spread",
  spreadTitle: "Financial spread",
  spreadSub: "Audited accounts FY2023 to FY2025, corporate template v7",
  columns: ["USD M", "FY2023", "FY2024", "FY2025", "Source"],
  rows: [
    { k: "Revenue", v: ["48.2", "52.9", "57.4"], src: "p. 14" },
    { k: "Gross profit", v: ["11.6", "12.8", "13.9"], src: "p. 14" },
    { k: "EBITDA", v: ["6.1", "6.9", "7.6"], src: "p. 22", hi: true },
    { k: "Net debt", v: ["18.4", "17.1", "15.9"], src: "p. 31" },
    { k: "Net debt to EBITDA", v: ["3.0x", "2.5x", "2.1x"], src: "calc", calc: true },
    { k: "Debt service cover", v: ["1.42x", "1.58x", "1.71x"], src: "calc", calc: true },
  ],
  check: "E-invoice revenue for FY2025 is USD 56.9M. Reported revenue matches within 0.9%.",
  memo: {
    title: "Committee memo, draft 2",
    status: "Checker pending",
    lead: "Renew the USD 20M working capital line for 12 months, with a net debt to EBITDA covenant of 3.0x.",
    body: "Leverage fell from 3.0x to 2.1x over three years as EBITDA grew 25% [p. 22]. Reported revenue agrees with e-invoice records [e-invoice summary].",
    facts: [
      ["Rating model", "Corporate PD v3.2"],
      ["Last validated", "12 Aug 2026"],
      ["Gini", "0.61"],
      ["Population stability", "0.04, stable"],
    ],
  },
};

export const spreadPanel = {
  title: "Kestrel Freight, FY2025 audited accounts",
  meta: "38 pages, 112 lines extracted",
  rows: [
    { k: "Revenue", v: "57,412", src: "p. 14", conf: 1 },
    { k: "Gross profit", v: "13,905", src: "p. 14", conf: 1 },
    { k: "Depreciation", v: "2,118", src: "p. 19", conf: 1 },
    { k: "Lease liabilities", v: "4,760", src: "p. 27", conf: 0.55, review: true },
    { k: "Bank borrowings", v: "16,245", src: "p. 31", conf: 1 },
    { k: "Cash and equivalents", v: "5,105", src: "p. 12", conf: 1 },
  ],
  chips: [
    { title: "E-invoice check", body: "Revenue within 0.9% of MyInvois records", tone: "ok" },
    { title: "1 line for review", body: "Lease note split across two pages", tone: "warn" },
  ],
};

export const memoPanel = {
  kicker: "Credit paper, corporate banking committee",
  title: "Kestrel Freight Holdings: renewal of working capital line",
  sections: [
    {
      h: "Recommendation",
      p: "Renew the USD 20M line for 12 months. Hold the net debt to EBITDA covenant at 3.0x and add quarterly e-invoice reporting.",
    },
    {
      h: "Financial analysis",
      p: "EBITDA grew from USD 6.1M to 7.6M over three years [accounts p. 22]. Debt service cover improved to 1.71x. Two customers make up 31% of revenue [note 8].",
    },
  ],
  bars: [
    { h: "Key risks", widths: [92, 84, 60] },
    { h: "Covenants", widths: [88, 70] },
  ],
  sources: [
    "Audited accounts FY2025, p. 22",
    "Audited accounts FY2025, note 8",
    "Facility agreement, clause 21.3",
    "E-invoice summary, FY2025",
  ],
  signoff: [
    ["Drafted", "Memo", "ok"],
    ["Maker", "Edited 9 lines", "ok"],
    ["Checker", "Pending", "warn"],
  ],
};

export const validatePanel = {
  title: "Model inventory",
  dossier: "BNM",
  columns: ["Model", "Type", "Last validated", "Gini", "PSI", "Status"],
  rows: [
    { m: "Corporate PD v3.2", t: "Rating", d: "12 Aug 2026", g: "0.61", p: "0.04", s: "Stable", tone: "ok" },
    { m: "SME application v5.0", t: "Scorecard", d: "3 Jul 2026", g: "0.54", p: "0.13", s: "Watch", tone: "warn", active: true },
    { m: "Retail behavioural v2.4", t: "Scorecard", d: "28 May 2026", g: "0.66", p: "0.03", s: "Stable", tone: "ok" },
    { m: "IFRS 9 PD term structure", t: "Provisioning", d: "15 Apr 2026", g: "n/a", p: "0.08", s: "Stable", tone: "ok" },
    { m: "Third-party application score", t: "Vendor", d: "19 Jun 2026", g: "0.47", p: "0.27", s: "Breach", tone: "bad" },
  ],
  focus: {
    title: "SME application v5.0",
    body: "Stability drifting since April. Validate opened a review and drafted the findings.",
    points: [98, 96, 94, 95, 88, 80, 72, 64, 58],
  },
};

// lifecycle grid
export const lifecycle = {
  heading: "One platform across the credit lifecycle.",
  body: "Eleven products for banks, Islamic banks and licensed lenders. Each one sells against a budget line you already hold.",
  stages: [
    { n: "1", name: "Origination", items: [{ n: "Spread", s: "Early access" }, { n: "Assess", s: "Early access" }] },
    { n: "2", name: "Underwriting", items: [{ n: "Memo", s: "Early access" }, { n: "Memory", s: "Later" }] },
    { n: "3", name: "Decision", items: [{ n: "Core", s: "Live" }, { n: "Islamic", s: "Later" }] },
    { n: "4", name: "Monitoring", items: [{ n: "Watch", s: "Next" }] },
    { n: "5", name: "Provisioning", items: [{ n: "Provision", s: "Later" }] },
    { n: "6", name: "Governance", items: [{ n: "Validate", s: "Live" }, { n: "Forge", s: "Next" }, { n: "Benchmark", s: "In research" }] },
  ],
};

// product screens
export const screens = {
  heading: "The screens your credit teams will live in.",
  cards: [
    {
      name: "Assess",
      sub: "Affordability for licensed consumer lenders",
      tag: "Early access",
      kind: "assess",
    },
    {
      name: "Watch",
      sub: "Early warning and covenant monitoring",
      tag: "Next",
      kind: "watch",
    },
    {
      name: "Forge",
      sub: "Bank-owned local models",
      tag: "Next",
      kind: "forge",
    },
    {
      name: "Provision",
      sub: "IFRS 9 expected credit loss and climate stress",
      tag: "Later",
      kind: "provision",
    },
  ],
};

export const assessData = {
  ref: "Application A-20931, instalment plan",
  time: "Received 10:42",
  income: 1840,
  commitments: 610,
  limitPct: 40,
  minInst: 40,
  maxInst: 260,
  defInst: 95,
  approveLimit: "USD 1,200",
  decisionSub: "Reasons recorded for the borrower and the regulator",
  ruleset: "Rule set v2.3",
};

export const watchData = {
  header: "Signals this week",
  scope: "Corporate book, 1,204 borrowers",
  filters: ["All", "Covenant", "News", "Behaviour"],
  signals: [
    { id: 1, cat: "Covenant", t: "Covenant headroom below 5%", b: "Borrower 1148, packaging. Net debt to EBITDA at 3.4x against a 3.5x limit.", detail: "Headroom fell after a debt-funded line upgrade in Q2. The relationship team has asked for a revised forecast.", tone: "warn" },
    { id: 2, cat: "News", t: "Supplier dispute in local press", b: "Borrower 0873, construction. Two Bahasa Malaysia sources, translated and linked.", detail: "A subcontractor filed a payment claim. No court date yet, and the exposure sits on a single project.", tone: "warn" },
    { id: 3, cat: "Behaviour", t: "Overdraft above 95% for 21 days", b: "Borrower 2210, wholesale. Relationship manager notified.", detail: "Utilisation has climbed since the festive season. Likely seasonal, but flagged for a closer look.", tone: "mute" },
    { id: 4, cat: "Covenant", t: "DSCR forecast below 1.20x", b: "Borrower 0642, logistics. Forward cover slips under the 1.20x floor next quarter.", detail: "Driven by a higher fuel-cost assumption. A sensitivity table is attached to the file.", tone: "warn" },
  ],
};

export const forgeData = {
  base: "Base model, 3B parameters",
  fingerprint: "signed 30 Aug 2026, fingerprint 7f3a...c21e",
  note: "Tuning data used for these adapters never left this environment. The adapters belong to the bank.",
  adapters: [
    { name: "Statement extraction", v: "v12", status: "Validated", tone: "ok", on: true },
    { name: "Credit memo writer", v: "v7", status: "Validated", tone: "ok", on: true },
    { name: "Local-language risk classifier", v: "v4", status: "In review", tone: "warn", on: true },
    { name: "Policy retrieval", v: "v3", status: "Validated", tone: "ok", on: false },
  ],
};

export const provisionData = {
  header: "Quarter-end run, Q3 2026",
  unit: "USD M",
  cols: ["Stage", "Exposure", "ECL", "Coverage"],
  rows: [
    { stage: "Stage 1", exp: 4812.0, ecl: 19.2, cov: 0.4 },
    { stage: "Stage 2", exp: 612.5, ecl: 28.8, cov: 4.7 },
    { stage: "Stage 3", exp: 138.4, ecl: 71.9, cov: 51.95 },
  ],
  scenarios: [
    { name: "Base", factor: 1.0, delta: "+0%", tone: "cream" },
    { name: "Orderly", factor: 1.06, delta: "+6%", tone: "cream" },
    { name: "Disorderly", factor: 1.14, delta: "+14%", tone: "amber" },
    { name: "Hot house", factor: 1.22, delta: "+22%", tone: "red" },
  ],
};

// perimeter / security
export const perimeter = {
  heading: "One-way by design.",
  body: "Base models are trained on public filings, regulatory texts and synthetic data, then signed. They enter your data center. Nothing about your borrowers comes back out.",
  foundry: { label: "Inferra foundry", title: "Public and synthetic data only" },
  arrow: "Signed weights, inbound",
  dc: {
    title: "Your data center",
    note: "No outbound path for borrower data",
    products: ["Core", "Spread", "Memo", "Validate", "Assess", "Watch", "Provision", "Forge"],
    stay: "Borrower data, statements, bureau files and model outputs stay here",
  },
  cols: [
    { h: "Small enough to run at home", b: "One to eight billion parameters, quantized for hardware your bank already owns." },
    { h: "Yours to keep", b: "Adapters tuned on your data belong to you and never train another bank's model." },
    { h: "Written for the supervisor", b: "Dossiers in Bank Negara Malaysia and Central Bank of the UAE formats." },
  ],
};

// roadmap
export const roadmap = {
  heading: "Shipping in order.",
  cols: [
    { label: "Live", tone: "live", items: ["Core", "Validate"] },
    { label: "Early access", tone: "early", items: ["Spread", "Memo", "Assess"] },
    { label: "Next", tone: "next", items: ["Watch", "Forge"] },
    { label: "Later", tone: "later", items: ["Provision", "Islamic", "Memory"] },
    { label: "In research", tone: "research", items: ["Benchmark"] },
  ],
};

// faq (codos has a Questions section)
export const faq = {
  heading: "Questions committees ask.",
  items: [
    {
      q: "Does any borrower data leave our perimeter?",
      a: "No. Base models are trained externally on public and synthetic data, signed, then shipped into your data center. There is no outbound path for borrower data, statements, bureau files or model outputs.",
    },
    {
      q: "What hardware do the models need?",
      a: "Inferra models are one to eight billion parameters and quantized to run on hardware most banks already own. No specialist GPU fleet is required to get to production.",
    },
    {
      q: "How does validation satisfy our regulator?",
      a: "Validate tracks discrimination, stability and drift continuously across your whole inventory, including third-party models. Dossiers export in Bank Negara Malaysia and Central Bank of the UAE formats.",
    },
    {
      q: "Who owns the tuned models?",
      a: "You do. Adapters tuned on your data belong to the bank and never train another institution's model. The tuning data never leaves your environment.",
    },
    {
      q: "What is the engagement model?",
      a: "Start with a model audit priced at cost, followed by an early-access deployment of Core and Validate, then a staged rollout across the credit lifecycle.",
    },
  ],
};

export const cta = {
  heading: "Start with a model audit, priced at cost.",
  primary: { label: "Book a model audit", href: "#briefing" },
  secondary: { label: "Request a briefing", href: "#briefing" },
};

export const footer = {
  brand: "inferra",
  blurb: "Credit AI that never leaves the building. Built by Pilot Multimedia.",
  copyright: "Copyright 2026 Inferra",
  groups: [
    { title: "Products", links: ["Core", "Validate", "Spread", "Memo", "Assess"] },
    { title: "Company", links: ["Security", "Roadmap", "Contact"] },
    { title: "Offices", links: ["Kuala Lumpur", "Singapore", "hello@inferra.ai"] },
  ],
};

export const tones = {
  ok: "var(--green)",
  warn: "var(--amber)",
  bad: "var(--red)",
  mute: "var(--muted)",
};
